(function initializeCartFamily(root, createApi) {
  'use strict';
  const api = createApi();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (!root?.document || root.AlvauraCartFamily) return;
  const parse = (value) => { try { return JSON.parse(value); } catch { return null; } };
  let changing = false;
  const sourceAttributes = ['data-cart-update-id', 'data-line', 'data-index', 'data-original-quantity'];
  const panel = (cartEdit) => cartEdit?.querySelector('[data-cart-family], [data-cart-line-edit]');
  const data = (element) => parse(element?.querySelector('[data-cart-family-data]')?.textContent);
  const showError = (element, key, review = false) => {
    const error = element?.querySelector('[data-cart-family-error]');
    if (!error) return;
    error.textContent = element.dataset[key] || element.dataset.errorLabel;
    error.hidden = false;
    if (review) {
      const link = root.document.createElement('a');
      link.href = element.dataset.cartUrl;
      link.textContent = element.dataset.viewLabel;
      error.append(' ', link);
    }
  };
  const variantId = (cartEdit) => cartEdit.querySelector('.product-edit-item input[name="id"]')?.value;
  const selectedOptions = (cartEdit) => {
    const picker = cartEdit.querySelector('variant-edit-selects');
    const selects = Array.from(picker?.querySelectorAll('select.variant-option__select') || []);
    return selects.length ? selects.map((select) => select.value)
      : Array.from(picker?.querySelectorAll('fieldset') || []).map((group) => group.querySelector('input:checked')?.value || '');
  };
  const quantity = (cartEdit) => Number(cartEdit.querySelector('.product-edit-item input[name="updates[]"]')?.value);
  const setBusy = (cartEdit, busy) => {
    cartEdit.setAttribute('aria-busy', String(busy));
    cartEdit.querySelectorAll('input, select, button').forEach((input) => {
      if (busy) { input.dataset.familyWasDisabled = String(input.disabled); input.disabled = true; }
      else if ('familyWasDisabled' in input.dataset) { input.disabled = input.dataset.familyWasDisabled === 'true'; delete input.dataset.familyWasDisabled; }
    });
    const button = cartEdit.closest('edit-cart-modal')?.querySelector('[data-update-cart-edit]');
    if (button) {
      if (busy && !('familyWasDisabled' in button.dataset)) { button.dataset.familyWasDisabled = String(button.disabled); button.disabled = true; }
      else if (!busy && 'familyWasDisabled' in button.dataset) { button.disabled = button.dataset.familyWasDisabled === 'true'; delete button.dataset.familyWasDisabled; }
      button.setAttribute('aria-busy', String(busy));
    }
  };
  const route = (value, kind) => api.cartRoute(value, root.location.href, kind);
  const request = async (path, body) => {
    const response = await root.fetch(path, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const result = await response.json();
    return { ok: response.ok && !result.errors && !result.status, data: result };
  };
  async function choose(event) {
    const button = event.target.closest?.('[data-cart-family-target]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const cartEdit = button.closest('[data-template-cart-edit]');
    const element = panel(cartEdit);
    if (!element || changing || cartEdit.dataset.familyTerminal === 'true') return;
    const family = data(element);
    const selection = api.selectSibling(family, variantId(cartEdit), button.dataset.cartFamilyTarget, selectedOptions(cartEdit));
    if (!selection) { showError(element, 'missingLabel'); return; }
    if (selection.product.id === family.current) return;
    const url = api.productUrl(selection.product.url, root.location.href, selection.variant.id);
    const count = quantity(cartEdit);
    if (!url || !api.validQuantity(selection.variant, count)) { showError(element, 'errorLabel'); return; }
    const container = cartEdit.closest('.halo-popup-content');
    const attributes = Object.fromEntries(sourceAttributes.map((name) => [name, cartEdit.getAttribute(name)]));
    changing = true;
    setBusy(cartEdit, true);
    try {
      const response = await root.fetch(url, { method: 'GET', credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Unavailable product');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = document.querySelector('[data-template-cart-edit]');
      const nextPanel = panel(next);
      const nextData = data(nextPanel);
      if (!next || !nextPanel || !element.dataset.familyId || nextPanel.dataset.familyId !== element.dataset.familyId
        || nextData?.current !== selection.product.id || String(variantId(next)) !== String(selection.variant.id)) throw new Error('Unresolved sibling');
      if (!cartEdit.isConnected || container.querySelector('[data-template-cart-edit]') !== cartEdit) return;
      container.innerHTML = document.body.innerHTML;
      const inserted = container.querySelector('[data-template-cart-edit]');
      sourceAttributes.forEach((name) => { if (attributes[name] != null) inserted.setAttribute(name, attributes[name]); });
      inserted.querySelector('input[name="updates[]"]').value = String(count);
      initialize(inserted);
      panel(inserted)?.querySelector(`[data-family-axis="${button.dataset.familyAxis}"][aria-pressed="true"]`)?.focus();
    } catch (_) {
      if (cartEdit.isConnected) showError(element, 'errorLabel');
    } finally {
      changing = false;
      if (cartEdit.isConnected) setBusy(cartEdit, false);
    }
  }
  function initialize(cartEdit) {
    if (!panel(cartEdit)) return;
    if (!cartEdit.hasAttribute('data-original-quantity')) cartEdit.setAttribute('data-original-quantity', String(quantity(cartEdit)));
  }
  async function submit(cartEdit) {
    const element = panel(cartEdit);
    if (!element || changing || cartEdit.dataset.familyTerminal === 'true') return;
    const family = data(element);
    const selection = api.selectCurrent(family, variantId(cartEdit), selectedOptions(cartEdit));
    const count = quantity(cartEdit);
    const urls = {
      get: route(element.dataset.cartUrl, 'get'), add: route(element.dataset.addUrl, 'add'), change: route(element.dataset.changeUrl, 'change')
    };
    if (!selection || !api.validQuantity(selection.variant, count) || Object.values(urls).some((url) => !url)) {
      showError(element, 'missingLabel'); return;
    }
    changing = true;
    setBusy(cartEdit, true);
    const result = await api.replaceLine({
      expected: { key: cartEdit.getAttribute('data-line'), id: cartEdit.getAttribute('data-cart-update-id'), quantity: Number(cartEdit.getAttribute('data-original-quantity')) },
      target: selection.variant, quantity: count,
      getCart: async () => { const response = await request(urls.get); if (!response.ok) throw new Error('Cart read failed'); return response.data; },
      add: (body) => request(urls.add, body), change: (body) => request(urls.change, body)
    });
    changing = false;
    if (result.ok) { root.location.reload(); return; }
    setBusy(cartEdit, false);
    if (result.uncertain) {
      cartEdit.dataset.familyTerminal = 'true';
      setBusy(cartEdit, true);
    }
    showError(element, result.uncertain ? 'reviewLabel' : result.code === 'stale' ? 'staleLabel' : result.code === 'unsupported' ? 'unsupportedLabel' : 'errorLabel', result.uncertain);
  }
  root.AlvauraCartFamily = { initialize, submit };
  root.document.addEventListener('click', choose, true);
})(typeof window === 'object' ? window : null, function createCartFamilyApi() {
  'use strict';
  const normalize = (value) => String(value ?? '').trim().replace(/\s+/g, ' ');
  const id = (value) => /^\d+$/.test(String(value));
  const properties = (line) => line?.properties && typeof line.properties === 'object' && !Array.isArray(line.properties) ? line.properties : {};
  const stableProperties = (value) => JSON.stringify(Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)));
  const sameIdentity = (a, b) => String(a.variant_id || a.id) === String(b.variant_id || b.id)
    && stableProperties(properties(a)) === stableProperties(properties(b))
    && !a.selling_plan_allocation && !b.selling_plan_allocation;
  const fingerprint = (line) => `${line.variant_id || line.id}:${stableProperties(properties(line))}:${line.quantity}:${line.selling_plan_allocation?.selling_plan?.id || ''}`;
  const unchangedOthers = (before, after, excluded) => {
    const list = (cart) => cart.items.filter((line) => !excluded(line)).map(fingerprint).sort();
    return JSON.stringify(list(before)) === JSON.stringify(list(after));
  };
  const metalAxis = (product) => {
    const explicit = product.options?.findIndex((name) => normalize(name).toLowerCase() === 'metal');
    if (explicit >= 0) return explicit;
    const legacy = product.options?.findIndex((name) => normalize(name).toLowerCase() === 'color');
    return legacy >= 0 && product.compositionMetal && product.variants.some((variant) => variant.options?.[legacy] === product.compositionMetal) ? legacy : -1;
  };
  function selectCurrent(family, variantId, selectedOptions) {
    if (!Array.isArray(family?.products) || !id(variantId)) return null;
    const products = family.products.filter((product) => product.id === family.current);
    if (products.length !== 1 || products[0].requiresSellingPlan || !Array.isArray(products[0].variants)) return null;
    const variants = products[0].variants.filter((variant) => String(variant.id) === String(variantId));
    if (variants.length !== 1) return null;
    const metalIndex = metalAxis(products[0]);
    if (metalIndex >= 0 && products[0].compositionMetal && variants[0].options?.[metalIndex] !== products[0].compositionMetal) return null;
    const expectedOptions = products[0].hasOnlyDefaultVariant && Array.isArray(selectedOptions) && selectedOptions.length === 0
      ? variants[0].options : selectedOptions;
    if (expectedOptions && (!Array.isArray(expectedOptions) || !Array.isArray(variants[0].options)
      || expectedOptions.length !== variants[0].options.length
      || expectedOptions.some((value, index) => normalize(value) !== normalize(variants[0].options[index])))) return null;
    return { product: products[0], variant: variants[0] };
  }
  function selectSibling(family, variantId, targetId, selectedOptions) {
    const current = selectCurrent(family, variantId, selectedOptions);
    if (!current || !id(targetId)) return null;
    const products = family.products.filter((product) => product.id === targetId);
    if (products.length !== 1) return null;
    const product = products[0];
    if (!product.metal || !product.gemstone || !Array.isArray(product.options) || !Array.isArray(product.variants)) return null;
    if (product.metal !== current.product.metal && product.gemstone !== current.product.gemstone) return null;
    const sourceSize = current.product.options?.findIndex((name) => normalize(name).toLowerCase() === 'size');
    const targetSize = product.options.findIndex((name) => normalize(name).toLowerCase() === 'size');
    if (!(sourceSize >= 0) || targetSize < 0) return null;
    const size = normalize(current.variant.options?.[sourceSize]);
    if (!size) return null;
    let candidates = product.variants.filter((variant) => normalize(variant.options?.[targetSize]) === size);
    const metalIndex = metalAxis(product);
    if (metalIndex >= 0 && product.compositionMetal) candidates = candidates.filter((variant) => variant.options?.[metalIndex] === product.compositionMetal);
    if (candidates.length !== 1 || !id(candidates[0].id) || candidates[0].available !== true) return null;
    return { product, variant: candidates[0] };
  }
  function validQuantity(variant, quantity) {
    return Number.isSafeInteger(quantity) && quantity > 0 && variant?.available === true
      && Number.isSafeInteger(variant.min) && variant.min > 0 && quantity >= variant.min
      && Number.isSafeInteger(variant.increment) && variant.increment > 0 && (quantity - variant.min) % variant.increment === 0
      && (variant.max == null || Number.isSafeInteger(variant.max) && quantity <= variant.max)
      && typeof variant.madeToOrder === 'boolean';
  }
  function productUrl(target, current, variantId) {
    try {
      const source = new URL(current), url = new URL(target, source);
      if (!target || url.origin !== source.origin || !/\/products\/[^/]+$/.test(url.pathname) || !id(variantId)) return null;
      url.search = '';
      url.searchParams.set('view', 'ajax_edit_cart');
      url.searchParams.set('variant', String(variantId));
      const preview = source.searchParams.get('preview_theme_id');
      if (preview && id(preview)) url.searchParams.set('preview_theme_id', preview);
      return url.href;
    } catch { return null; }
  }
  function cartRoute(target, current, kind) {
    try {
      const source = new URL(current), url = new URL(target, source);
      const suffix = kind === 'get' ? '' : `/${kind}`;
      if (!target || url.origin !== source.origin || !new RegExp(`/cart${suffix}(?:\\.js)?$`).test(url.pathname)) return null;
      if (!url.pathname.endsWith('.js')) url.pathname += '.js';
      url.search = '';
      return url.href;
    } catch { return null; }
  }
  const unsupported = (line) => Boolean(line.selling_plan_allocation || line.parent_relationship || line.has_components || line.item_components?.length
    || (line.properties != null && (typeof line.properties !== 'object' || Array.isArray(line.properties) && line.properties.length > 0))
    || line.components?.length || Object.keys(properties(line)).some((key) => /bundle|bundler|selling.?plan|subscription|component/i.test(key)));
  const validCart = (cart) => Array.isArray(cart?.items) && cart.items.every((line) => typeof line.key === 'string' && id(line.variant_id || line.id) && Number.isSafeInteger(line.quantity) && line.quantity > 0);
  async function replaceLine({ expected, target, quantity, getCart, add, change }) {
    if (!expected?.key || !id(expected.id) || !Number.isSafeInteger(expected.quantity) || expected.quantity < 1 || !id(target?.id) || !validQuantity(target, quantity)) return { ok: false, code: 'invalid' };
    let before;
    try { before = await getCart(); } catch { return { ok: false, code: 'read' }; }
    if (!validCart(before)) return { ok: false, code: 'read' };
    const originals = before.items.filter((line) => line.key === expected.key);
    if (originals.length !== 1 || String(originals[0].variant_id || originals[0].id) !== String(expected.id) || originals[0].quantity !== expected.quantity) return { ok: false, code: 'stale' };
    const original = originals[0];
    if (unsupported(original) || before.items.filter((line) => sameIdentity(line, original)).length !== 1) return { ok: false, code: 'unsupported' };
    const targetProperties = { ...properties(original) };
    if (targetProperties.Fulfillment === 'Made to Order') delete targetProperties.Fulfillment;
    if (target.madeToOrder) targetProperties.Fulfillment = 'Made to Order';
    const destination = { id: target.id, properties: targetProperties };
    if (sameIdentity(original, destination)) {
      if (quantity === original.quantity) return { ok: true, cart: before };
      try {
        const result = await change({ id: original.key, quantity });
        const after = await getCart();
        if (!result.ok || !validCart(after)) return { ok: false, code: 'change', uncertain: true };
        const changed = after.items.filter((line) => sameIdentity(line, destination));
        if (changed.length !== 1 || changed[0].quantity !== quantity || !unchangedOthers(before, after, (line) => sameIdentity(line, destination))) return { ok: false, code: 'change', uncertain: true };
        return { ok: true, cart: after };
      } catch { return { ok: false, code: 'change', uncertain: true }; }
    }
    const existingTargets = before.items.filter((line) => sameIdentity(line, destination));
    if (existingTargets.length > 1 || existingTargets.some(unsupported)) return { ok: false, code: 'unsupported' };
    const previousTargetQuantity = existingTargets.reduce((sum, line) => sum + line.quantity, 0);
    try {
      const result = await add({ items: [{ id: target.id, quantity, properties: targetProperties }] });
      const afterAdd = await getCart();
      if (!result.ok || !validCart(afterAdd)) return { ok: false, code: 'add', uncertain: true };
      const added = afterAdd.items.filter((line) => sameIdentity(line, destination));
      const sources = afterAdd.items.filter((line) => sameIdentity(line, original));
      if (added.length !== 1 || added[0].quantity !== previousTargetQuantity + quantity || sources.length !== 1 || sources[0].quantity !== original.quantity
        || !unchangedOthers(before, afterAdd, (line) => sameIdentity(line, destination))) return { ok: false, code: 'concurrent', uncertain: true };
      const resultRemove = await change({ id: sources[0].key, quantity: 0 });
      const after = await getCart();
      if (!resultRemove.ok || !validCart(after) || after.items.some((line) => sameIdentity(line, original))
        || !unchangedOthers(afterAdd, after, (line) => sameIdentity(line, original))) return { ok: false, code: 'remove', uncertain: true };
      return { ok: true, cart: after };
    } catch { return { ok: false, code: 'write', uncertain: true }; }
  }
  return { selectCurrent, selectSibling, validQuantity, productUrl, cartRoute, replaceLine };
});
